import { FormControl, InputLabel, MenuItem, Select, type SelectChangeEvent } from '@mui/material';
import { useCallback, useMemo } from 'react';
import type { WidgetProps } from '../../../schema-form/types.js';
import { getWidgetId, useWidgetTitle } from '../../../schema-form/util/widget.js';
import coerceValue from '../../utils/coerce-value.js';
import getWidgetOptions, { type WidgetOption } from '../../utils/get-widget-options.js';
import FieldInfo from '../field-info/index.js';
import Widget from './widget.js';

type OptionValue = string | number;
export type DropdownProps = Pick<
  WidgetProps,
  'schema' | 'config' | 'error' | 'isRequired' | 'disabled' | 'readOnly' | 'onChange' | 'onBlur' | 'path' | 'formName'
> & {
  readonly value: any;
  readonly widgets?: any;
  readonly size?: 'small' | 'medium';
};

function DropdownWidget(props: DropdownProps) {
  const { schema, config, widgets, value, error, isRequired, disabled, readOnly, size, onChange, onBlur } = props;
  const handleChange = useCallback(
    (event: SelectChangeEvent) => {
      onChange(coerceValue(schema, event.target.value));
      onBlur();
    },
    [onBlur, onChange, schema]
  );

  const Template = widgets && config && widgets[config.get('itemTemplate')];

  const renderItem = useCallback(
    (options: WidgetOption, testIdPrefix: string) => {
      const value = options.value as OptionValue;
      const testId = `${testIdPrefix}-${value}`;
      if (Template) {
        return (
          <MenuItem key={value} value={value} data-testid={testId}>
            <Template {...options} />
          </MenuItem>
        );
      }

      return (
        <MenuItem key={value} value={value} data-testid={testId}>
          {options.label}
        </MenuItem>
      );
    },
    [Template]
  );

  const options: WidgetOption[] = useMemo(() => {
    const options = getWidgetOptions(schema, config);
    const hiddenValues = config?.get('hiddenValues')?.toJS();
    if (hiddenValues) {
      const hiddenValueSet = new Set<OptionValue>(hiddenValues);
      return options.filter((item) => item.value === value || !hiddenValueSet.has(item.value as OptionValue));
    }

    return options;
  }, [schema, config, value]);

  const allowBlank = config?.get('allowBlank');
  const blankTitle = config?.get('blankTitle') ?? '';
  const title = useWidgetTitle(props);
  const testId = getWidgetId(props);
  const selectSize = size ?? 'small';
  const labelSize = selectSize === 'medium' ? 'normal' : 'small';

  return (
    <Widget testId={testId}>
      <FormControl fullWidth variant="outlined" error={Boolean(error)} required={isRequired} disabled={disabled}>
        <InputLabel id={testId} size={labelSize}>
          {title}
        </InputLabel>
        <Select
          size={selectSize}
          data-testid={`${testId}__select`}
          variant="outlined"
          labelId={testId}
          label={title}
          value={value?.toJS ? value.toJS() : value}
          onChange={handleChange}
          inputProps={{ readOnly }}
        >
          {allowBlank && (
            <MenuItem key="" value="">
              {blankTitle}
            </MenuItem>
          )}
          {options.map((option) => renderItem(option, testId))}
        </Select>
        <FieldInfo instructions={config?.get('instructions')} description={schema?.get('description')} error={error} />
      </FormControl>
    </Widget>
  );
}

export default DropdownWidget;
